import { useAuthStore } from '@/store/auth'

/**
 * Simple fetch wrapper that adds authorization header with access token
 * and supports caching and timeouts
 */
export const fetchWithAuth = async <T>(
  url: string,
  options: RequestInit & {
    timeout?: number
  } = {},
): Promise<T> => {
  const { timeout = 10_000, ...fetchOptions } = options

  try {
    // Clone headers to avoid modifying the original object
    const headers = new Headers(fetchOptions.headers || {})

    // Add authorization header if we have a token
    const token = await useAuthStore.getState().getAccessToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Create an AbortController for timeout handling
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    // Make the request with the updated headers and timeout
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    })

    // Clear the timeout
    clearTimeout(timeoutId)

    // Handle errors
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    // Parse response based on content type
    const contentType = response.headers.get('content-type')
    let data: T
    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    } else {
      data = (await response.text()) as unknown as T
    }

    return data
  } catch (error) {
    console.error(error)
    throw error
  }
}
